import { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import useSEO from "../hooks/useSEO";
import { api } from "../lib/api";
import { qjCatBySlug, QJ_CATEGORIES } from "../lib/qj";
import QJModelCard from "../components/QJModelCard";
import QJInquiryModal from "../components/QJInquiryModal";

export default function QJCategorie() {
  const { slug } = useParams();
  const cat = qjCatBySlug(slug);
  const [models, setModels] = useState(null);
  const [testrit, setTestrit] = useState(null);

  useSEO({
    title: cat ? `QJ Motor ${cat.label} — TT Motorsports Torhout` : "QJ Motor — TT Motorsports",
    description: cat ? `Alle QJ Motor ${cat.label}-modellen bij TT Motorsports in Torhout, met prijzen incl. btw en testritaanvraag.` : "",
  });

  useEffect(() => {
    if (!cat) return;
    setModels(null);
    api.get("/qj-models", { params: { categorie: cat.label } }).then((r) => setModels(r.data)).catch(() => setModels([]));
  }, [slug]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!cat) return <Navigate to="/qj-motor" replace />;

  return (
    <div data-testid="qj-category-page" className="bg-[#0A0A0B] min-h-screen pt-28 md:pt-32 pb-20">
      <div className="max-w-[1400px] mx-auto px-5 md:px-8">
        <Link to="/qj-motor" data-testid="qj-cat-back" className="inline-flex items-center gap-2 text-[#9A9CA3] hover:text-[#E10600] font-display uppercase text-sm font-600 mb-8 transition-colors">
          <ArrowLeft size={16} /> Alle categorieën
        </Link>
        <div className="flex flex-wrap items-end justify-between gap-4 mb-10 md:mb-12">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6 min-w-0">
            <img src="/qj/qjmotor-logo.png" alt="QJ MOTOR" className="h-4 sm:h-6 md:h-8 w-auto self-start sm:self-auto" />
            <h1 data-testid="qj-cat-title" className="heading-xl text-4xl md:text-6xl text-white sm:border-l border-[#2A2B31] sm:pl-6">{cat.label}</h1>
          </div>
          <div className="flex flex-wrap gap-2" data-testid="qj-cat-switch">
            {QJ_CATEGORIES.map((c) => (
              <Link key={c.slug} to={`/qj-motor/categorie/${c.slug}`} data-testid={`qj-cat-switch-${c.slug}`}
                className={`font-display uppercase text-xs font-700 tracking-wider px-3 min-h-[44px] inline-flex items-center transition-colors ${c.slug === slug ? "text-[#E10600]" : "text-[#9A9CA3] hover:text-white"}`}>
                {c.label}
              </Link>
            ))}
          </div>
        </div>

        {models === null && <p className="text-[#9A9CA3] font-mono-tt">Laden...</p>}
        {models !== null && models.length === 0 && <p data-testid="qj-empty" className="text-[#9A9CA3]">Momenteel geen modellen in deze categorie.</p>}

        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-5 md:gap-7" data-testid="qj-grid">
          {(models || []).map((m, i) => (
            <motion.div key={m.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(i, 9) * 0.05 }} className="flex">
              <QJModelCard m={m} onTestrit={setTestrit} fromCat={slug} />
            </motion.div>
          ))}
        </div>
      </div>
      {testrit && <QJInquiryModal model={testrit} mode="testrit" onClose={() => setTestrit(null)} />}
    </div>
  );
}
